import { Button, cn } from '@quadralize/ui'
import { AltArrowLeft } from '@solar-icons/react'
import React, { type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../navigation/breadcrumb/breadcrumb'

export interface PageHeaderBreadcrumbItem {
  label: string
  path?: string
}

interface PageHeaderProps {
  title: string
  mobileTitle?: string
  description?: string
  mobileDescription?: string
  actions?: ReactNode
  mobileActionsInline?: boolean
  breadcrumbs?: PageHeaderBreadcrumbItem[]
  backPath?: string
  onBack?: () => void
  className?: string
}

export function PageHeader({
  title,
  mobileTitle,
  description,
  mobileDescription,
  actions,
  mobileActionsInline = false,
  breadcrumbs,
  backPath,
  onBack,
  className,
}: PageHeaderProps) {
  const navigate = useNavigate()
  const shouldInlineActions = mobileActionsInline && !!actions

  const handleBack = () => {
    if (onBack) {
      onBack()
      return
    }
    if (backPath) {
      navigate(backPath)
    }
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-col gap-1.5">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumb className="mb-0.5">
            <BreadcrumbList>
              {breadcrumbs.map((item, index) => (
                <React.Fragment key={item.label}>
                  <BreadcrumbItem>
                    {item.path ? (
                      <BreadcrumbLink asChild>
                        <Link to={item.path}>{item.label}</Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage>{item.label}</BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                  {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                </React.Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        )}

        <div
          className={cn(
            'flex flex-col md:gap-4 lg:flex-row lg:items-center lg:justify-between',
            shouldInlineActions && 'gap-2',
          )}
        >
          <div
            className={cn(
              'flex items-center gap-4 max-w-3xl',
              shouldInlineActions && 'min-w-0 w-full lg:flex-1',
            )}
          >
            {(backPath || onBack) && (
              <Button
                variant="outline"
                size="icon"
                onClick={handleBack}
                className="h-10 w-10 shrink-0 rounded-full border-border-default hover:border-primary/50 hover:bg-primary/5 text-text-tertiary hover:text-primary transition-all duration-300 active:scale-95 shadow-sm"
              >
                <AltArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <div className={cn('flex flex-col', shouldInlineActions && 'min-w-0 w-full')}>
              {shouldInlineActions ? (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h1
                      className={cn(
                        'text-xl md:text-2xl font-bold leading-tight text-text-primary tracking-tight',
                        mobileTitle && 'hidden sm:block',
                      )}
                    >
                      {title}
                    </h1>
                    {mobileTitle && (
                      <h1 className="text-xl sm:hidden font-bold leading-tight text-text-primary tracking-tight">
                        {mobileTitle}
                      </h1>
                    )}
                  </div>
                  <div className="shrink-0 md:hidden">{actions}</div>
                </div>
              ) : (
                <>
                  <h1
                    className={cn(
                      'text-xl md:text-2xl font-bold leading-tight text-text-primary tracking-tight',
                      mobileTitle && 'hidden sm:block',
                    )}
                  >
                    {title}
                  </h1>
                  {mobileTitle && (
                    <h1 className="text-xl sm:hidden font-bold leading-tight text-text-primary tracking-tight">
                      {mobileTitle}
                    </h1>
                  )}
                </>
              )}
              {description && (
                <p
                  className={cn(
                    'text-xs sm:text-sm md:text-sm text-text-secondary leading-relaxed',
                    mobileDescription && 'hidden sm:block',
                  )}
                >
                  {description}
                </p>
              )}
              {mobileDescription && (
                <p className="text-xs sm:hidden text-text-secondary leading-relaxed">
                  {mobileDescription}
                </p>
              )}
            </div>
          </div>

          {actions && (
            <div
              className={cn(
                'flex items-center gap-2 md:gap-3 flex-wrap',
                shouldInlineActions ? 'hidden md:flex md:shrink-0 md:justify-end' : 'justify-end',
              )}
            >
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
